import { Button, Nav, NavItem, Collapse } from "reactstrap";
import { Link, useLocation } from "react-router-dom";
import { useState, useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";
import usePermissions from "../hooks/usePermissions";
import "./StaffSidebar.css"; // Reuse the same styling

const GeneralDashboardSidebar = () => {
  const location = useLocation();
  const { userType } = useAuth();
  const {
    permissions,
    hasPermission,
    hasAnyPermission,
    canViewUsers,
    canManageUsers,
    canViewFunders,
    canManageFunders,
    canCreateFunders,
    canViewBursaries,
    canManageBursaries,
    canViewBookings,
    canViewReports,
    canManagePermissions,
    canManageFunderOfficers,
    canManageFunderRepresentatives,
    canViewFunderApplications,
    canViewFunderAgreements,
    canViewFunderPayments,
    loading: permissionsLoading
  } = usePermissions();

  // Create user object for compatibility
  const user = { user_type: userType };

  const [openDropdowns, setOpenDropdowns] = useState({});

  // Generate navigation based on user permissions
  const navigation = useMemo(() => {
    console.log('Sidebar useMemo triggered, permissionsLoading:', permissionsLoading);
    console.log('Current permissions array:', permissions);
    console.log('Permissions length:', permissions?.length);
    
    if (permissionsLoading) {
      console.log('Permissions still loading, returning empty nav');
      return [];
    }

    const navItems = [
      {
        title: "Dashboard",
        href: "/general-dashboard",
        icon: "bi bi-speedometer2",
        show: true // Always show dashboard
      }
    ];

    // User Management Section
    if (canViewUsers() || canManageUsers()) {
      const userManagementChildren = [];
      
      if (canViewUsers() || canManageUsers()) {
        userManagementChildren.push({
          title: "All Users",
          href: "/general-dashboard/users",
          icon: "bi bi-people",
        });
      }

      if (user?.user_type === 'bmo_user_admin' && canManagePermissions()) {
        userManagementChildren.push({
          title: "User Permissions",
          href: "/general-dashboard/user-permissions",
          icon: "bi bi-shield-check",
        });
      }

      if (userManagementChildren.length > 0) {
        navItems.push({
          title: "User Management",
          icon: "bi bi-people",
          hasDropdown: true,
          children: userManagementChildren,
          show: true
        });
      }
    }

    // Funders Section - Only show if user has ANY funder-related permissions
    console.log('=== FUNDER PERMISSION DEBUG ===');
    console.log('User email:', userType);
    console.log('Permissions loading:', permissionsLoading);
    console.log('Total permissions count:', permissions?.length);
    console.log('All permissions:', permissions?.map(p => p.codename));
    
    console.log('Permission function values:', {
      canViewFunders: canViewFunders(),
      canManageFunders: canManageFunders(),
      canCreateFunders: canCreateFunders(),
      canManageFunderOfficers: canManageFunderOfficers(),
      canManageFunderRepresentatives: canManageFunderRepresentatives(),
      canViewFunderApplications: canViewFunderApplications(),
      canViewFunderAgreements: canViewFunderAgreements(),
      canViewFunderPayments: canViewFunderPayments()
    });
    
    // Check individual permissions that make up canViewFunders
    console.log('Individual permission checks for canViewFunders:');
    console.log('hasPermission("view_funder"):', hasPermission('view_funder'));
    console.log('hasPermission("add_funder"):', hasPermission('add_funder'));
    console.log('hasPermission("change_funder"):', hasPermission('change_funder'));
    
    // Also check the raw hasAnyPermission call - using the ACTUAL permission names
    const hasRawFunderPermissions = hasAnyPermission([
      'add_funder', 'change_funder', 'view_funder', 'delete_funder',
      'add_funderofficer', 'change_funderofficer', 'view_funderofficer', 'delete_funderofficer',
      'add_funderrepresentative', 'change_funderrepresentative', 'view_funderrepresentative', 'delete_funderrepresentative',
      'add_funderapplication', 'change_funderapplication', 'view_funderapplication', 'delete_funderapplication',
      'add_funderagreement', 'change_funderagreement', 'view_funderagreement', 'delete_funderagreement',
      'add_funderpayment', 'change_funderpayment', 'view_funderpayment', 'delete_funderpayment'
    ]);
    
    console.log('hasRawFunderPermissions (using actual names):', hasRawFunderPermissions);
    
    const hasFunderPermissions = canViewFunders() || canManageFunders() || canCreateFunders() || 
                                canManageFunderOfficers() || canManageFunderRepresentatives() || 
                                canViewFunderApplications() || canViewFunderAgreements() || 
                                canViewFunderPayments() || hasRawFunderPermissions;

    console.log('Final hasFunderPermissions result:', hasFunderPermissions);
    console.log('=== END FUNDER PERMISSION DEBUG ===');

    // Add single Funders link if user has any funder permissions
    if (hasFunderPermissions) {
      console.log('ADDING FUNDERS LINK TO SIDEBAR');
      navItems.push({
        title: "Funders",
        href: "/general-dashboard/funders",
        icon: "bi bi-building",
        show: true
      });
    } else {
      console.log('NOT ADDING FUNDERS LINK - USER HAS NO FUNDER PERMISSIONS');
    }

    // Bursaries Section - Simple link only
    if (canViewBursaries() || canManageBursaries()) {
      navItems.push({
        title: "Bursaries",
        href: "/general-dashboard/bursaries",
        icon: "bi bi-award",
        show: true
      });
    }

    // Bookings Section - Permission-based visibility
    if (canViewBookings()) {
      navItems.push({
        title: "Bookings",
        href: "/general-dashboard/bookings",
        icon: "bi bi-calendar-check",
        show: true
      });
    }

    // Reports Section
    if (canViewReports()) {
      const reportsChildren = [];

      if (hasAnyPermission(['view_funder_reports', 'generate_funder_reports'])) {
        reportsChildren.push({
          title: "Funder Reports",
          href: "/general-dashboard/reports/funders",
          icon: "bi bi-graph-up",
        });
      }

      if (hasPermission('view_disbursement_records')) {
        reportsChildren.push({
          title: "Financial Reports",
          href: "/general-dashboard/reports/financial",
          icon: "bi bi-bar-chart",
        });
      }

      if (hasAnyPermission(['view_user', 'view_students'])) {
        reportsChildren.push({
          title: "Student Reports",
          href: "/general-dashboard/reports/students",
          icon: "bi bi-people",
        });
      }

      if (reportsChildren.length > 0) {
        navItems.push({
          title: "Reports",
          icon: "bi bi-graph-up",
          hasDropdown: true,
          children: reportsChildren,
          show: true
        });
      }
    }

    // Always show Profile and Settings
    navItems.push(
      {
        title: "Profile",
        href: "/general-dashboard/profile",
        icon: "bi bi-person-circle",
        show: true
      },
      {
        title: "Documents",
        href: "/general-dashboard/documents",
        icon: "bi bi-file-earmark-text",
        show: true
      },
      {
        title: "Notifications",
        href: "/general-dashboard/notifications",
        icon: "bi bi-bell",
        show: true
      },
      {
        title: "Settings",
        href: "/general-dashboard/settings",
        icon: "bi bi-gear-wide-connected",
        show: true
      }
    );

    return navItems.filter(item => item.show);
  }, [
    permissions,
    permissionsLoading,
    canViewUsers,
    canManageUsers,
    canViewFunders,
    canManageFunders,
    canCreateFunders,
    canViewBursaries,
    canManageBursaries,
    canViewBookings,
    canViewReports,
    canManagePermissions,
    canManageFunderOfficers,
    canManageFunderRepresentatives,
    canViewFunderApplications,
    canViewFunderAgreements,
    canViewFunderPayments,
    hasPermission,
    hasAnyPermission,
    userType,
    user?.user_type
  ]);

  const toggleDropdown = (index) => {
    setOpenDropdowns(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const closeSidebar = () => {
    document.getElementById("sidebarArea").classList.toggle("showSidebar");
  };

  return (
    <div className="p-3">
      {/* Mobile close button - only visible on small screens */}
      <div className="d-flex justify-content-end d-lg-none mb-2">
        <Button
          color="link"
          size="sm"
          className="text-dark p-1"
          onClick={closeSidebar}
          style={{ border: 'none' }}
        >
          <i className="bi bi-x-lg" style={{ fontSize: '1.2rem', color: '#000' }}></i>
        </Button>
      </div>
      
      <div className="d-flex align-items-center">
      </div>
      <div className="pt-4 mt-2">
        <Nav vertical className="sidebarNav">
          {navigation.map((navi, index) => (
            <NavItem key={index} className="sidenav-bg">
              {navi.hasDropdown ? (
                <>
                  <div
                    className="nav-link py-3 d-flex justify-content-between align-items-center dropdown-parent"
                    style={{ 
                      color: "#000", 
                      cursor: "pointer",
                      backgroundColor: openDropdowns[index] ? "rgb(230 238 248)" : "transparent"
                    }}
                    onClick={() => toggleDropdown(index)}
                  >
                    <div>
                      <i className={navi.icon} style={{ color: "#000" }}></i>
                      <span className="ms-3 d-inline-block" style={{ 
                        fontSize: navi.title.length > 12 ? "12px" : "inherit",
                        fontWeight: navi.title.length > 12 ? "600" : "normal"
                      }}>{navi.title}</span>
                    </div>
                    <i className={`bi ${openDropdowns[index] ? 'bi-chevron-up' : 'bi-chevron-down'}`} 
                       style={{ color: "#000" }}></i>
                  </div>
                  <Collapse isOpen={openDropdowns[index]}>
                    <div style={{ backgroundColor: "rgb(230 238 248)", borderRadius: "4px", margin: "0 8px" }}>
                      {navi.children && navi.children.map((child, childIndex) => (
                        <Link
                          key={childIndex}
                          to={child.href}
                          className="nav-link py-2 ps-3"
                          style={{ 
                            color: "#000", 
                            fontSize: "0.9rem",
                            backgroundColor: "transparent"
                          }}
                        >
                          <i className={child.icon} style={{ color: "#000" }}></i>
                          <span className="ms-2 d-inline-block" style={{ 
                            fontSize: child.title.length > 12 ? "12px" : "inherit",
                            fontWeight: child.title.length > 12 ? "600" : "normal"
                          }}>{child.title}</span>
                        </Link>
                      ))}
                    </div>
                  </Collapse>
                </>
              ) : (
                <Link
                  to={navi.href}
                  className={
                    location.pathname === navi.href
                      ? "nav-link py-3"
                      : "nav-link py-3"
                  }
                  style={{ color: "#000" }}
                >
                  <i className={navi.icon} style={{ color: "#000" }}></i>
                  <span className="ms-3 d-inline-block" style={{ 
                    fontSize: navi.title.length > 12 ? "12px" : "inherit",
                    fontWeight: navi.title.length > 12 ? "600" : "normal"
                  }}>{navi.title}</span>
                </Link>
              )}
            </NavItem>
          ))}
        </Nav>
      </div>
    </div>
  );
};

export default GeneralDashboardSidebar;
